import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import propTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Button from '@material-ui/core/Button';
import { useSelector, useDispatch } from 'react-redux';
import { states } from 'api/data/states';
import PageLoader from 'components/PageLoader';
import ProjectDetails from './components/ProjectDetails';
// import { geoCoder } from 'api/geocomplete';
import styles from './styles';
import { postCreateProject, postUpdateProject } from 'store/projectTypes/projectTypesOperations';
import _ from 'lodash';
import { MEASUREMENT_SYSTEM } from 'common/measurements';
import projectTypesActions from 'store/projectTypes/projectTypesActions';
import {
  fetchLaborClassificationsForProject,
  selectProjectRates,
} from 'store/laborClassifications';
import useUnsavedChangesPrompt from 'common/hooks/useUnsavedChangesPrompt';
import UnsavedChangesModal from 'components/OptionalModal/UnsavedChangesModal';

// Shared by submit and the unsaved-changes check so both compare the same shape.
const buildProjectPayload = (values, documents) => {
  // REPLACE-ALL: BE deletes any rate row whose LC isn't in this array.
  // Constrain to chipped LCs — sending one for an unchipped LC is a 400.
  const defaultLcId = values.defaultLaborClassificationId || null;
  const alternateLcIds = values.alternateLaborClassifications || [];
  const chippedLcIds = new Set([defaultLcId, ...alternateLcIds].filter(Boolean));
  const ratesMap = values.laborClassificationRates || {};
  const laborClassificationRates = Array.from(chippedLcIds)
    .filter((lcId) => ratesMap[lcId])
    .map((lcId) => {
      const r = ratesMap[lcId];
      return {
        laborClassificationId: lcId,
        earningsCode: r.earningsCode ?? null,
        baseRate: parseFloat(r.baseRate) ?? null,
        fringeBenefit: parseFloat(r.fringeBenefit) ?? null,
      };
    });

  return {
    active: values.active,
    jobNumber: values.jobNumber,
    type: values.type,
    rated: values.rated,
    route: values.route,
    section: values.section,
    shift: values.shift,
    contractorId: values.contractorId,
    state: values.state,
    county: values.county,
    id: values.id,
    locations: values.locations.filter((location) => location.address.trim()),
    rallyPoints: values.rallyPoints.filter((location) => location.address.trim()),
    documentStorage:
      (documents || [])
        .filter((doc) => doc.name.trim() && doc.url.trim())
        .map((doc) => ({
          name: doc.name.trim(),
          url: doc.url.trim(),
        })) || [],
    measurement: values.measurement,
    dwlVerificationContacts: values.dwlVerificationContacts || [],
    assignedToLocations: values.assignedToLocations,
    defaultLaborClassificationId: defaultLcId,
    alternateLaborClassifications: alternateLcIds,
    laborClassificationRates,
  };
};

function validateURL(url) {
  const urlPattern = /^(https?:\/\/)?([\w\-]+\.)+[\w\-]+(\/[\w\- .\/?%&=+*!'();:@$,]+)?$/;
  return urlPattern.test(url);
}

const validation = (values) => {
  const errors = {};
  if (!values.contractorId.trim()) {
    errors.contractorId = "This field can't be blank";
  }
  if (!values.route.trim()) {
    errors.route = "This field can't be blank";
  }
  if (!values.jobNumber.trim()) {
    errors.jobNumber = "This field can't be blank";
  }
  if (!values.type.trim()) {
    errors.type = "This field can't be blank";
  }
  if (
    values.defaultLaborClassificationId &&
    Array.isArray(values.alternateLaborClassifications) &&
    values.alternateLaborClassifications.includes(values.defaultLaborClassificationId)
  ) {
    errors.alternateLaborClassifications =
      'Default and Alternate Labor Classifications must not overlap';
  }
  const locations = {};
  values.locations.forEach((el, i) => {
    if (!el.name.trim() && !el.address.trim() && !el.zipcode.trim()) return; //if 3 location fields are empty - it's not an error

    const locationsErr = {};
    if (!el.name.trim()) {
      locationsErr.name = "This field can't be blank";
    }
    if (!el.address.trim()) {
      locationsErr.address = "This field can't be blank";
    }
    if (Object.keys(locationsErr).length) {
      locations[i] = locationsErr;
    }
  });
  if (Object.keys(locations).length) {
    errors.locations = locations;
  }

  const rallyPoints = {};
  values.rallyPoints.forEach((el, i) => {
    if (!el.name.trim() && !el.address.trim() && !el.zipcode.trim()) return; //if 3 location fields are empty - it's not an error

    const locationsErr = {};
    if (!el.name.trim()) {
      locationsErr.name = "This field can't be blank";
    }
    if (!el.address.trim()) {
      locationsErr.address = "This field can't be blank";
    }
    if (Object.keys(locationsErr).length) {
      rallyPoints[i] = locationsErr;
    }
  });
  if (Object.keys(rallyPoints).length) {
    errors.rallyPoints = rallyPoints;
  }
  const newDocument = {};

  if (values.newDocument.name.trim() || values.newDocument.url.trim()) {
    if (!values.newDocument.name.trim()) {
      newDocument.name = "This field can't be blank";
    }
    if (values.newDocument.url.trim() && !validateURL(values.newDocument.url)) {
      newDocument.url = 'This field must be a URL address';
    }
    if (!values.newDocument.url.trim()) {
      newDocument.url = "This field can't be blank";
    }
  }

  if (Object.keys(newDocument).length) {
    errors.newDocument = newDocument;
  }

  if (!values?.assignedToLocations?.length) {
    errors.assignedToLocations = 'Project should be assigned to at least 1 location';
  }

  return errors;
};

const ProjectForm = ({
  classes,
  closeDrawer,
  openSnackbar,
  project,
  type,
  setDisplayContractorLocationAdded,
  requestCloseRef,
}) => {
  const contractors = useSelector((store) => store.contractors.data);
  const settings = useSelector((store) => store.personalProfile?.organization?.settings)
  const { _id: locationId, settings: locationSettings } = useSelector((store) => store.personalProfile?.organizationLocation || {})
  // Snapshot of stored rates at first render seeds Formik initialValues. The
  // async fetch in the effect below populates the slice; LaborClassificationsSection
  // hydrates Formik values once the fetch resolves.
  const initialLcRates = useSelector((store) => selectProjectRates(store, project?._id));
  const dispatch = useDispatch();
  const formikRef = useRef();
  const [statesData, setStatesData] = useState([]);
  const [fullyProjectsDocuments, setFullyProjectsDocuments] = useState([]);
  const [addForm, setAddForm] = useState(false);

  useEffect(() => {
    if (project) {
      setFullyProjectsDocuments(
        (project?.documentStorage?.length && project.documentStorage) || []
      );
    }
  }, [project]);

  useEffect(() => {
    if (project?._id) {
      dispatch(fetchLaborClassificationsForProject(project._id)).catch(() => {});
    }
  }, [project?._id, dispatch]);

  const handleSelectDocument = (setFieldValue) => (e) => {
    const id = e.target.value;

    if (id === 'add' || id === '') {
      handleDocumentAddForm(true);
      setFieldValue('selectedDocumentId', '');
      setFieldValue('newDocument', {
        name: '',
        url: '',
      });
      return;
    }

    handleDocumentAddForm(true);
    const currentFullyContact = fullyProjectsDocuments.find((el) => el.name === id);
    setFieldValue('newDocument.name', currentFullyContact.name);
    setFieldValue('newDocument.url', currentFullyContact.url);
    setFieldValue('selectedDocumentId', id);
  };

  const handleDocumentAddForm = (value) => {
    setAddForm(value);
  };

  useEffect(() => {
    setStatesData(states);
  }, []);

  const removeLocationFields = (arrayHelpers, index, values) => () => {
    if (values[arrayHelpers.name].length == 1) {
      arrayHelpers.remove(index);
      arrayHelpers.push({
        name: '',
        address: '',
        zipcode: '',
        type: arrayHelpers.name === 'rallyPoints' ? 'rallyPoint' : 'projectLocation',
      });
    } else {
      arrayHelpers.remove(index);
    }
  };

  const getLatLong = (setFieldValue, index) => (latLong) => {
    setFieldValue(`locations[${index}].address`, latLong);
  };

  const handleChangeAddress = (setFieldValue, index, values, arrayHelpers, fieldName) => (e) => {
    setFieldValue(`${fieldName}[${index}].address`, e.target.value);
    if (values[fieldName].length === index + 1) {
      arrayHelpers.push({
        name: '',
        address: '',
        zipcode: '',
        type: fieldName === 'rallyPoints' ? 'rallyPoint' : 'projectLocation',
      });
    }
    // geoCoder(e.target.value, getLatLong(setFieldValue, index));//!epic mock
  };

  const handleTimeFieldsKeyDown = (e) => {
    if (!((e.keyCode >= 48 && e.keyCode <= 57) || e.keyCode === 8)) {
      e.preventDefault();
    }
  };

  const handleSelectContractor = (setFieldValue) => (contractorId) => {
    setFieldValue('contractorId', contractorId ?? '');
  };

  const createUpdateCb = (res) => {
    if (res?.data?.locations?.length) {
      setDisplayContractorLocationAdded({
        isOpen: true,
        locations: res?.data?.locations,
        project: res?.data?.project,
      });
    };

    if (!res?.data?.assignedToLocations?.includes(locationId)) {
      dispatch(projectTypesActions.deleteProject(res?.data?._id));
    }
  }

  const initialValues = project
    ? {
        jobNumber: project.jobNumber,
        type: project.type,
        rated: project.rated,
        route: project.route,
        section: project.section || '',
        shift: project.shift,
        contractorId: project.contractorId,
        state: project.state || '',
        county: project.county || '',
        id: project.id || '',
        locations: [
          ...(project.locations || []),
          {
            name: '',
            address: '',
            zipcode: '',
            type: 'projectLocation',
          },
        ],
        rallyPoints: [
          ...(project.rallyPoints || []),
          {
            name: '',
            address: '',
            zipcode: '',
            type: 'rallyPoint',
          },
        ],
        isUpdateType: true,
        active: project.active,
        documentStorage: project.documentStorage || [],
        selectedDocumentId: '',
        newDocument: {
          name: '',
          url: '',
        },
        measurement: project.measurement || settings?.measurement || MEASUREMENT_SYSTEM.imperial,
        dwlVerificationContacts: project.dwlVerificationContacts || [],
        assignedToLocations: project.assignedToLocations || [locationId],
        defaultLaborClassificationId: project.defaultLaborClassificationId ?? null,
        alternateLaborClassifications: project.alternateLaborClassifications ?? [],
        laborClassificationRates: { ...initialLcRates },
      }
    : {
        jobNumber: '',
        type: '',
        rated: false,
        route: '',
        section: '',
        shift: 'Day',
        contractorId: '',
        state: '',
        county: '',
        id: '',
        locations: [
          {
            name: '',
            address: '',
            zipcode: '',
            type: 'projectLocation',
          },
        ],
        rallyPoints: [
          {
            name: '',
            address: '',
            zipcode: '',
            type: 'rallyPoint',
          },
        ],
        isUpdateType: false,
        active: true,
        documentStorage: [],
        selectedDocumentId: '',
        newDocument: {
          name: '',
          url: '',
        },
        measurement: settings?.measurement || MEASUREMENT_SYSTEM.imperial,
        dwlVerificationContacts: [],
        assignedToLocations: [locationId],
        defaultLaborClassificationId: null,
        alternateLaborClassifications: [],
        laborClassificationRates: {},
      };

  // Baseline built from the original project + live LC-rates slice. We compare
  // against this rather than Formik's `dirty` flag because LaborClassificationsSection
  // hydrates `laborClassificationRates` asynchronously after mount, which would
  // otherwise make an untouched form look dirty.
  const initialPayload = useMemo(
    () => buildProjectPayload(initialValues, project?.documentStorage || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project, initialLcRates, locationId, settings]
  );

  const getIsDirty = useCallback(() => {
    const formik = formikRef.current;
    if (!formik) return false;
    return !_.isEqual(
      buildProjectPayload(formik.values, fullyProjectsDocuments),
      initialPayload
    );
  }, [fullyProjectsDocuments, initialPayload]);

  const handleSaveFromPrompt = useCallback(() => {
    formikRef.current?.submitForm();
  }, []);

  const { requestClose, isPromptOpen, closePrompt, handleDiscard, handleSave } =
    useUnsavedChangesPrompt({
      getIsDirty,
      onClose: closeDrawer,
      onSave: handleSaveFromPrompt,
    });

  // Expose the guarded close so the parent Drawer's backdrop/Escape also prompts.
  useEffect(() => {
    if (!requestCloseRef) return undefined;
    requestCloseRef.current = requestClose;
    return () => {
      requestCloseRef.current = null;
    };
  }, [requestCloseRef, requestClose]);

  return (
    <div className={classes.formWrapper} role="presentation">
      <Formik
        innerRef={formikRef}
        validate={validation}
        validateOnChange={false}
        validateOnBlur={true}
        initialValues={initialValues}
        // enableReinitialize={true}
        onSubmit={async (values, { setFieldError, setSubmitting }) => {
          const data = buildProjectPayload(values, fullyProjectsDocuments);

          const handleSubmitError = (e) => {
            const message = e?.response?.data?.message || e?.message || 'Save failed';
            openSnackbar('error', message);
            setSubmitting(false);
            if (e?.error === 'job-number-not-unique') {
              setFieldError('jobNumber', 'This Job Number is already taken');
            }
            if (message === 'Default and Alternate Labor Classifications must not overlap') {
              setFieldError('alternateLaborClassifications', message);
            }
            if (message === 'Labor Classification does not exist') {
              setFieldError('defaultLaborClassificationId', message);
            }
          };

          if (type === 'create') {
            try {
              await dispatch(postCreateProject(data, createUpdateCb));
              closeDrawer();
              openSnackbar('success', 'Successfully Added!');
            } catch (e) {
              handleSubmitError(e);
            }
          } else {
            try {
              await dispatch(postUpdateProject(data, project._id, createUpdateCb));
              closeDrawer();
              openSnackbar('success', 'Successfully Updated!');
            } catch (e) {
              handleSubmitError(e);
            }
          }
        }}
      >
        {({
          errors,
          touched,
          values,
          handleChange,
          handleBlur,
          isSubmitting,
          setFieldValue,
          setErrors,
          validateForm,
        }) => (
          <PageLoader loading={false}>
            {/* <PageLoader loading={isSubmitting}> */}
            <Form autoComplete="off" className={classes.formFlex}>
              <div>
                <Typography variant="h3" style={{ marginBottom: '40px' }}>
                  {type === 'create' ? 'Create' : 'Edit'} Project
                </Typography>

                <IconButton
                  className={classes.closeButton}
                  onClick={requestClose}
                  aria-label="Close"
                  disableTouchRipple
                >
                  <CloseIcon />
                </IconButton>

                <ProjectDetails
                  contractors={contractors}
                  openSnackbar={openSnackbar}
                  isSubmitting={isSubmitting}
                  project={project}
                  states={statesData}
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  removeLocationFields={removeLocationFields}
                  handleChangeAddress={handleChangeAddress}
                  type={type}
                  handleTimeFieldsKeyDown={handleTimeFieldsKeyDown}
                  handleSelectContractor={handleSelectContractor}
                  setErrors={setErrors}
                  validateForm={validateForm}
                  fullyProjectsDocuments={fullyProjectsDocuments}
                  handleSelectDocument={handleSelectDocument}
                  addForm={addForm}
                  handleDocumentAddForm={handleDocumentAddForm}
                  setFullyProjectsDocuments={setFullyProjectsDocuments}
                  locationSettings={locationSettings}
                />
              </div>

              <div className={classes.buttonHolder}>
                <Button
                  color="secondary"
                  variant="outlined"
                  className={classes.cancelButton}
                  onClick={requestClose}
                  disabled={isSubmitting}
                  disableTouchRipple
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="outlined"
                  className={classes.saveButton}
                  disabled={isSubmitting}
                  disableTouchRipple
                >
                  Save
                </Button>
              </div>
            </Form>
          </PageLoader>
        )}
      </Formik>
      <UnsavedChangesModal
        isOpen={isPromptOpen}
        onClose={closePrompt}
        onDiscard={handleDiscard}
        onSave={handleSave}
      />
    </div>
  );
};

ProjectForm.propTypes = {
  classes: propTypes.object.isRequired,
  closeDrawer: propTypes.func.isRequired,
  openSnackbar: propTypes.func.isRequired,
  type: propTypes.string.isRequired,
  project: propTypes.object,
  requestCloseRef: propTypes.object,
};

export default withStyles(styles)(ProjectForm);
